/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationsRemoveUser } from "../funcs/organizationsRemoveUser.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RemoveOrganizationUserRequest,
  RemoveOrganizationUserSecurity,
} from "../models/operations/removeorganizationuser.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoveOrganizationUserMutationVariables = {
  request: RemoveOrganizationUserRequest;
  security?: RemoveOrganizationUserSecurity | undefined;
  options?: RequestOptions;
};

export type RemoveOrganizationUserMutationData = void;

export type RemoveOrganizationUserMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * removeUser organizations
 *
 * @remarks
 * Remove a user from the active organization in Speakeasy and delete their WorkOS organization membership.
 */
export function useRemoveOrganizationUserMutation(
  options?: MutationHookOptions<
    RemoveOrganizationUserMutationData,
    RemoveOrganizationUserMutationError,
    RemoveOrganizationUserMutationVariables
  >,
): UseMutationResult<
  RemoveOrganizationUserMutationData,
  RemoveOrganizationUserMutationError,
  RemoveOrganizationUserMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoveOrganizationUserMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoveOrganizationUser(): MutationKey {
  return ["@gram/client", "organizations", "removeUser"];
}

export function buildRemoveOrganizationUserMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoveOrganizationUserMutationVariables,
  ) => Promise<RemoveOrganizationUserMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoveOrganizationUser(),
    mutationFn: function removeOrganizationUserMutationFn({
      request,
      security,
      options,
    }): Promise<RemoveOrganizationUserMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationsRemoveUser(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
